import type { OrderTicketTaxQuotaRequest } from '@interfaces/ticketevolution/tikets';
import { getTicketsTaxQuotes } from '@pages/api/te/tax';

export const TICKET_TAX_QUOTE_TTL_MS = 2 * 60 * 1000;
export const TICKET_TAX_QUOTE_HOVER_DELAY_MS = 100;
const MAX_CACHED_QUOTES = 20;

export type TicketTaxQuote = {
  tax_signature?: string | null;
  wholesale?: {
    tax?: number | string | null;
    additional_expense?: number | string | null;
    service_fee?: number | string | null;
    shipping?: number | string | null;
  } | null;
};

type CacheEntry = {
  promise: Promise<TicketTaxQuote>;
  expiresAt: number;
};

const quoteCache = new Map<string, CacheEntry>();
const scheduledPrefetches = new Map<string, ReturnType<typeof setTimeout>>();

function taxQuoteCacheKey(ticketGroupId: number, quantity: number) {
  return `${ticketGroupId}:${quantity}`;
}

function isValidTaxQuoteRequest(ticketGroupId: number, quantity: number) {
  return (
    Number.isInteger(ticketGroupId) &&
    ticketGroupId > 0 &&
    Number.isInteger(quantity) &&
    quantity > 0
  );
}

function touchCacheEntry(key: string, entry: CacheEntry) {
  quoteCache.delete(key);
  quoteCache.set(key, entry);
}

function evictOldestQuotes() {
  while (quoteCache.size > MAX_CACHED_QUOTES) {
    const oldestKey = quoteCache.keys().next().value;
    if (oldestKey === undefined) return;
    quoteCache.delete(oldestKey);
  }
}

function getCachedQuote(key: string) {
  const entry = quoteCache.get(key);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    quoteCache.delete(key);
    return null;
  }
  touchCacheEntry(key, entry);
  return entry;
}

export function sumWholesaleTicketTax(
  quote: TicketTaxQuote | null | undefined
) {
  const wholesale = quote?.wholesale;
  if (!wholesale) return 0;

  return [
    wholesale.tax,
    wholesale.additional_expense,
    wholesale.service_fee,
    wholesale.shipping,
  ].reduce((sum, value) => sum + Number(value || 0), 0);
}

export function getTicketTaxQuote(
  ticketGroupId: number,
  quantity: number
): Promise<TicketTaxQuote> {
  if (!isValidTaxQuoteRequest(ticketGroupId, quantity)) {
    return Promise.reject(new Error('Invalid ticket tax quote request'));
  }

  const key = taxQuoteCacheKey(ticketGroupId, quantity);
  const cached = getCachedQuote(key);
  if (cached) return cached.promise;

  const request = {
    ticketGroupId,
    quantity,
  } satisfies OrderTicketTaxQuotaRequest;

  const promise = getTicketsTaxQuotes(request).then(
    (quote) => quote as TicketTaxQuote,
    (error: unknown) => {
      quoteCache.delete(key);
      throw error;
    }
  );

  quoteCache.set(key, {
    promise,
    expiresAt: Date.now() + TICKET_TAX_QUOTE_TTL_MS,
  });
  evictOldestQuotes();
  return promise;
}

export function prefetchTicketTaxQuote(
  ticketGroupId: number,
  quantity: number
) {
  if (!isValidTaxQuoteRequest(ticketGroupId, quantity)) return;
  void getTicketTaxQuote(ticketGroupId, quantity).catch(() => {});
}

export function scheduleTicketTaxQuotePrefetch(
  ticketGroupId: number,
  quantity: number
) {
  if (!isValidTaxQuoteRequest(ticketGroupId, quantity)) return;

  const key = taxQuoteCacheKey(ticketGroupId, quantity);
  if (getCachedQuote(key) || scheduledPrefetches.has(key)) return;

  scheduledPrefetches.set(
    key,
    setTimeout(() => {
      scheduledPrefetches.delete(key);
      prefetchTicketTaxQuote(ticketGroupId, quantity);
    }, TICKET_TAX_QUOTE_HOVER_DELAY_MS)
  );
}

export function cancelScheduledTicketTaxQuotePrefetch(
  ticketGroupId: number,
  quantity: number
) {
  const key = taxQuoteCacheKey(ticketGroupId, quantity);
  const timer = scheduledPrefetches.get(key);
  if (!timer) return;
  clearTimeout(timer);
  scheduledPrefetches.delete(key);
}

export function resetTicketTaxQuoteCache() {
  quoteCache.clear();
  scheduledPrefetches.forEach((timer) => clearTimeout(timer));
  scheduledPrefetches.clear();
}
